import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { EnvironmentOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Slider,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import * as R from 'ramda';

import AddressEntry from '@totem/components/portfolio/buildings/common/AddressEntry';
import {
  AddEditBuilding,
  Address,
} from '@totem/components/portfolio/buildings/common/types';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AddressFormErrors } from '@totem/types/address';
import { BuildingTypes, InvestmentPosture } from '@totem/types/building';
import { isNotNull } from '@totem/utilities/common';
import {
  BUILDINGS_ENDPOINT,
  CONFIGURATION_ENDPOINT,
  V2_BUILDINGS_ENDPOINT,
} from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

import './buildingCommon.css';
const FormItem = Form.Item;
const { Option } = Select;
const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
};
type ValidationResponse = {
  isValid: boolean;
  errors: AddressFormErrors;
};
type Props = {
  open: boolean;
  loading: boolean;
  buildingData: AddEditBuilding;
  onClose: (boolean) => void;
};
const AddEditDialog = ({ open, loading, buildingData, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { regions, doRefresh } = useContext(OrganizationContext);
  const [buildingTypes, setBuildingTypes] = useState<BuildingTypes>({
    id: '62a8d9d5134444b27707dda8',
    type: 'BuildingTypes',
    data: [
      '',
      'Commercial Office',
      'Data Center',
      'Educational',
      'Life Sciences',
      'Multi-Family High Rise',
      'Multi-Family Podium',
      'Retail',
      'Warehouse',
      'Other',
    ],
  });
  const [investmentPostures, setInvestmentPostures] = useState<
    InvestmentPosture[]
  >([
    { name: 'Conservative', description: '' },
    { name: 'Aggressive', description: '' },
    { name: 'Balanced', description: '' },
  ]);
  const [step, setStep] = useState<number>(0);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [errors, setErrors] = useState<AddressFormErrors>({});
  const [editData, setEditData] = useState<AddEditBuilding>(buildingData);
  const isEdit = buildingData.id !== '';
  const isFirstStep = step === 0;
  const isLastStep = step === 2;

  useEffect(() => {
    fetch(`${BUILDINGS_ENDPOINT}/types`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: BuildingTypes) => {
        setBuildingTypes(result);
      });
  }, [getApiToken]);

  useEffect(() => {
    fetch(`${CONFIGURATION_ENDPOINT}/investmentPostures`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: InvestmentPosture[]) => {
        setInvestmentPostures(result);
      });
  }, [getApiToken]);

  const sendUpdateBuilding = useCallback(
    async (payload: AddEditBuilding) => {
      if (isSending) {
        return;
      }
      setIsSending(true);
      const endPoint =
        payload.id !== ''
          ? `${V2_BUILDINGS_ENDPOINT}/${payload.id}`
          : `${V2_BUILDINGS_ENDPOINT}/create`;
      fetch(endPoint, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      }).then((res) => {
        CheckResponseShowError(res);

        setIsSending(false);

        onClose(true);

        doRefresh();
      });
    },
    [getApiToken],
  );

  const isFirstStepValid = (): ValidationResponse => {
    const newErrors: AddressFormErrors = {};
    if (R.isEmpty(editData.name) || R.isNil(editData.name)) {
      newErrors.name = 'Name is required.';
    }
    if (R.isEmpty(editData.region.id) || R.isNil(editData.region.id)) {
      newErrors.region = `Region is required.`;
    }
    setErrors(newErrors);
    return {
      isValid: R.isEmpty(newErrors),
      errors: newErrors,
    };
  };
  const isAddressStepValid = () => {
    const newErrors: AddressFormErrors = {};
    if (
      R.isEmpty(editData.address.addressOne) ||
      R.isNil(editData.address.addressOne)
    ) {
      newErrors.addressOne = `Address is required.`;
    }
    if (R.isEmpty(editData.address.city) || R.isNil(editData.address.city)) {
      newErrors.city = `City is required.`;
    }
    if (R.isEmpty(editData.address.state) || R.isNil(editData.address.state)) {
      newErrors.state = `State is required.`;
    }
    if (
      R.isEmpty(editData.address.country) ||
      R.isNil(editData.address.country)
    ) {
      newErrors.country = `Country is required.`;
    }
    if (
      R.isEmpty(editData.address.zipCode) ||
      R.isNil(editData.address.zipCode)
    ) {
      newErrors.zipCode = `Postal Code is required.`;
    }
    setErrors(newErrors);
    return R.isEmpty(newErrors);
  };

  const handleBack = () => {
    if (step === 0) {
      onClose(false);
    } else {
      setStep(step - 1);
    }
  };

  const handleNext = () => {
    if (step === 0) {
      const validation = isFirstStepValid();
      if (!validation.isValid) {
        logger.error('Not Valid');
        return;
      }
      setStep(1);
    } else if (step === 1) {
      setStep(2);
    } else if (isAddressStepValid()) {
      sendUpdateBuilding(editData);
    } else {
      logger.error('Address Step Not Valid');

      logger.debug(JSON.stringify(errors));
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleAddressChange = (newAddress: Address) => {
    setEditData({ ...editData, address: newAddress });
  };

  const handleRegionChange = (id: string) => {
    const region = regions.find((chk) => chk.id === id);
    setEditData({
      ...editData,
      region: {
        id: isNotNull(region) ? region.id : id,
        name: isNotNull(region) ? region.name : '',
      },
    });
  };

  const handleAttributeNumberChange = (name: string, value: number) => {
    setEditData({
      ...editData,
      attributes: { ...editData.attributes, [name]: value },
    });
  };

  const handleAttributeStringChange = (name: string, value: string) => {
    setEditData({
      ...editData,
      attributes: { ...editData.attributes, [name]: value },
    });
  };

  const handleAttributeDateChange = (name: string, value: Dayjs | null) => {
    setEditData({
      ...editData,
      attributes: {
        ...editData.attributes,
        [name]: value ? value.toDate() : undefined,
      },
    });
  };

  return (
    <div>
      <Modal
        title={buildingData.id === '' ? 'Add Building' : 'Update Building'}
        open={open}
        onCancel={() => onClose(false)}
        footer={
          <>
            <Button onClick={() => handleBack()}>
              {isFirstStep ? 'Cancel' : 'Back'}
            </Button>
            <Button type="primary" onClick={handleNext} loading={loading}>
              {isLastStep ? (isEdit ? 'Update' : 'Create') : 'Next'}
            </Button>
          </>
        }
      >
        <div styleName="modal-container">
          {step === 0 && (
            <>
              <div styleName="avatar-container">
                <Avatar
                  size="large"
                  icon={<EnvironmentOutlined />}
                  shape="circle"
                />
              </div>
              <Form layout="vertical" style={styles.form}>
                <FormItem
                  label="Region"
                  colon={false}
                  validateStatus={R.isNil(errors.region) ? 'success' : 'error'}
                  help={R.isNil(errors.region) ? null : errors.region}
                  style={styles.formItem}
                >
                  <Select
                    showSearch
                    filterOption
                    optionFilterProp="children"
                    onChange={handleRegionChange}
                    value={editData.region.id}
                    //disabled={isEdit}
                  >
                    {regions.map(({ id, name }) => {
                      return (
                        <Option key={id} value={id}>
                          {name}
                        </Option>
                      );
                    })}
                  </Select>
                </FormItem>
                <FormItem
                  label="Building Name"
                  colon={false}
                  validateStatus={R.isNil(errors.name) ? 'success' : 'error'}
                  help={R.isNil(errors.name) ? null : errors.name}
                  style={styles.formItem}
                >
                  <Input
                    disabled={loading}
                    value={editData.name}
                    name="name"
                    onChange={handleInputChange}
                  />
                </FormItem>
              </Form>
            </>
          )}
          {step === 1 && (
            <Form layout="vertical" style={styles.form}>
              <FormItem
                label="Building Type"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <Select
                  value={editData.attributes.type}
                  onChange={(value: string) =>
                    handleAttributeStringChange('type', value)
                  }
                >
                  {buildingTypes.data.map((bt) => (
                    <Option key={bt} value={bt}>
                      {bt}
                    </Option>
                  ))}
                </Select>
              </FormItem>
              <FormItem
                label="Square Footage"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <InputNumber
                  value={editData.attributes.squareFootage}
                  onChange={(value: number) =>
                    handleAttributeNumberChange('squareFootage', value)
                  }
                  min={0}
                />
              </FormItem>
              <FormItem
                label="Timezone"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <Input
                  value={editData.attributes.timezone}
                  onChange={(evt) =>
                    handleAttributeStringChange('timezone', evt.target.value)
                  }
                />
              </FormItem>
              <FormItem
                label="Investment Posture"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <Select
                  value={editData.attributes.investmentPosture}
                  onChange={(value: string) =>
                    handleAttributeStringChange('investmentPosture', value)
                  }
                >
                  {investmentPostures.map(({ name }) => (
                    <Option key={name} value={name}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
              <FormItem
                label="Location Risk"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <Slider
                  min={1}
                  max={10}
                  value={editData.attributes.locationRisk ?? 5}
                  onChange={(value: number) =>
                    handleAttributeNumberChange('locationRisk', value)
                  }
                />
              </FormItem>
              <FormItem
                label="Tenant Profile"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <Slider
                  min={1}
                  max={10}
                  value={editData.attributes.tenantProfile ?? 5}
                  onChange={(value: number) =>
                    handleAttributeNumberChange('tenantProfile', value)
                  }
                />
              </FormItem>
              <FormItem
                label="Occupancy Rate (%)"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <InputNumber
                  value={editData.attributes.occupancyRate}
                  onChange={(value: number) =>
                    handleAttributeNumberChange('occupancyRate', value)
                  }
                  min={0}
                  max={100}
                />
              </FormItem>
              <FormItem
                label="Investment Discount Rate (%)"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <InputNumber
                  value={editData.attributes.investmentDiscountRate}
                  onChange={(value: number) =>
                    handleAttributeNumberChange('investmentDiscountRate', value)
                  }
                  min={0}
                  max={100}
                />
              </FormItem>
              <FormItem
                label="Hold Period End Date"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <DatePicker
                  value={
                    editData.attributes.holdPeriodEndDate
                      ? dayjs(editData.attributes.holdPeriodEndDate)
                      : null
                  }
                  onChange={(value: Dayjs | null) =>
                    handleAttributeDateChange('holdPeriodEndDate', value)
                  }
                  style={{ width: '100%' }}
                />
              </FormItem>
            </Form>
          )}
          {step === 2 && (
            <AddressEntry
              address={editData.address}
              onChange={handleAddressChange}
              disabled={loading}
              errors={errors}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};
export default AddEditDialog;
